import { supabase } from '@/integrations/supabase/client';

/**
 * Extract the raw storage path from a value that might be a signed URL, public URL, or raw path.
 * Returns the bucket-relative path (e.g. "clientId-timestamp.png").
 */
export function extractStoragePath(bucket: string, value: string): string | null {
  if (!value) return null;
  // Already a raw path (no protocol)
  if (!value.startsWith('http') && !value.startsWith('data:') && !value.startsWith('/')) {
    return value;
  }
  // Try to extract path from Supabase storage URL patterns
  // Pattern: /storage/v1/object/sign/<bucket>/<path>?token=...
  // Pattern: /storage/v1/object/public/<bucket>/<path>
  const patterns = [
    new RegExp(`/storage/v1/object/sign/${bucket}/([^?]+)`),
    new RegExp(`/storage/v1/object/public/${bucket}/([^?]+)`),
    new RegExp(`/storage/v1/object/${bucket}/([^?]+)`),
  ];
  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match?.[1]) return decodeURIComponent(match[1]);
  }
  return null;
}

/**
 * Generate a signed URL for a file in a private storage bucket.
 * Falls back to the storage path if signing fails.
 * @param bucket - The storage bucket name
 * @param path - The file path within the bucket
 * @param expiresIn - Expiration time in seconds (default: 1 hour)
 */
export async function getSignedStorageUrl(
  bucket: string,
  path: string,
  expiresIn: number = 3600
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresIn);

  if (error) {
    console.error(`Failed to create signed URL for ${bucket}/${path}:`, error);
    throw error;
  }

  return data.signedUrl;
}

/**
 * Resolve a photo value (raw path or expired signed URL) to a fresh signed URL.
 */
export async function resolvePhotoUrl(value: string | null | undefined): Promise<string | null> {
  if (!value) return null;
  // Skip data URIs (base64 previews)
  if (value.startsWith('data:')) return value;
  // Skip blob URLs (local previews)
  if (value.startsWith('blob:')) return value;
  const storagePath = extractStoragePath('client-photos', value);
  if (!storagePath) {
    console.warn('[resolvePhotoUrl] Could not extract storage path from:', value);
    return null;
  }
  try {
    return await getSignedStorageUrl('client-photos', storagePath, 3600);
  } catch (err) {
    console.error('[resolvePhotoUrl] Failed to sign URL for path:', storagePath, err);
    return null;
  }
}
